import React, { useEffect, useCallback, useState, useRef } from "react";
import { Banner, Card, Text, TextField, Select, Spinner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import DataTable from "components/datatable/Datatable";
import Loading from "components/loading/Loading";
import { useNavigate, useParams } from "react-router-dom";
import useIsPhone from "features/files/use-is-phone";
import { fetch as fetchApprovalsPage } from "services/pending-approvals.service";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

type AssignedUserOption = { id: string; name: string };

type ListFilters = {
  textSearch: string;
  assignedUserId: string;
  status: string;
};

interface PendingApprovalsContainerProps {}

const PendingApprovalsContainer: React.FC<PendingApprovalsContainerProps> = () => {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();

  const [collection, setCollection] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [textSearch, setTextSearch] = useState("");
  const [appliedTextSearch, setAppliedTextSearch] = useState("");
  const [assignedUserFilter, setAssignedUserFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedUserOptions, setAssignedUserOptions] = useState<AssignedUserOption[]>(
    []
  );
  const [statusOptions, setStatusOptions] = useState<string[]>([]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const listRequestRef = useRef(0);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const appliedFilters: ListFilters = {
    textSearch: appliedTextSearch,
    assignedUserId: assignedUserFilter,
    status: statusFilter,
  };
  const filtersKey = `${appliedTextSearch}|${assignedUserFilter}|${statusFilter}`;

  const hasActiveFilters = Boolean(
    appliedTextSearch.trim() || assignedUserFilter || statusFilter
  );
  const isSearchPending =
    textSearch.trim().length > 0 &&
    textSearch.trim().length < MIN_SEARCH_LENGTH &&
    textSearch.trim() !== appliedTextSearch;

  hasMoreRef.current = hasMore;
  loadingRef.current = loading;
  loadingMoreRef.current = loadingMore;

  const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    setScrollRoot(node);
  }, []);

  // Debounced search
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      const trimmed = textSearch.trim();
      if (trimmed.length === 0 || trimmed.length >= MIN_SEARCH_LENGTH) {
        setAppliedTextSearch(trimmed);
      } else {
        setAppliedTextSearch("");
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [textSearch]);

  const loadPage = useCallback(
    async (targetPage: number, append: boolean) => {
      if (!companyId) return;
      const requestId = ++listRequestRef.current;

      try {
        if (append) {
          loadingMoreRef.current = true;
          setLoadingMore(true);
        } else {
          loadingRef.current = true;
          setLoading(true);
          setLoadError(false);
        }

        const data = await fetchApprovalsPage(companyId, {
          page: targetPage,
          pageSize: PAGE_SIZE,
          textSearch: appliedFilters.textSearch,
          assignedUserId: appliedFilters.assignedUserId,
          status: appliedFilters.status,
        });

        if (requestId !== listRequestRef.current) return;

        const entries = data.collection || [];
        if (data.filterOptions?.assignedUsers) {
          setAssignedUserOptions(data.filterOptions.assignedUsers);
        }
        if (data.filterOptions?.statuses) {
          setStatusOptions(data.filterOptions.statuses);
        }

        const nextHasMore = Boolean(data.pagination?.hasMore);
        setCollection((current) => (append ? [...current, ...entries] : entries));
        setHasMore(nextHasMore);
        hasMoreRef.current = nextHasMore;
        pageRef.current = targetPage;


        if (!append) {
          setHasLoadedOnce(true);
        }
      } catch (err) {
        if (requestId !== listRequestRef.current) return;
        console.error("Failed to load pending approvals list", err);
        setLoadError(true);
        if (!append) {
          setCollection([]);
          setHasMore(false);
          hasMoreRef.current = false;
          pageRef.current = 1;
        }
      } finally {
        if (requestId === listRequestRef.current) {
          loadingRef.current = false;
          loadingMoreRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [
      companyId,
      appliedFilters.textSearch,
      appliedFilters.assignedUserId,
      appliedFilters.status,
    ]
  );

  const tryLoadMore = useCallback(() => {
    if (!hasMoreRef.current || loadingRef.current || loadingMoreRef.current) {
      return;
    }
    loadPage(pageRef.current + 1, true);
  }, [loadPage]);

  // Refetch first page when filters or the active company change
  useEffect(() => {
    pageRef.current = 1;
    hasMoreRef.current = false;
    setHasMore(false);
    if (!companyId) {
      setCollection([]);
      setHasMore(false);
      setLoading(false);
      setHasLoadedOnce(true);
      return;
    }
    loadPage(1, false);
  }, [filtersKey, companyId, loadPage]);

  // On a phone the page itself scrolls (no second scroll box inside it), so the list's
  // visible bottom is the window's; on wider screens it is the inner box's.
  // Re-check sentinel after list updates
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const frameId = window.requestAnimationFrame(() => {
      const root = scrollRoot;
      const sentinel = sentinelRef.current;
      if (!root || !sentinel || !hasMoreRef.current) return;

      const visibleBottom = isPhone
        ? window.innerHeight
        : root.getBoundingClientRect().bottom;
      const sentinelRect = sentinel.getBoundingClientRect();
      if (sentinelRect.top <= visibleBottom + 200) {
        tryLoadMore();
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [scrollRoot, isPhone, collection.length, hasMore, loading, loadingMore, filtersKey, tryLoadMore]);

  // Infinite scroll observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!scrollRoot || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          tryLoadMore();
        }
      },
      { root: isPhone ? null : scrollRoot, rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [scrollRoot, isPhone, tryLoadMore, collection.length, filtersKey]);

  const approvalsCollection =
    collection
      ?.filter((item: any) => item.customer && item.customer?.id)
      .map((item: any) => ({
        id: item.finance?.id || item.id,
        financeId: item.finance?.id,
        company:
          item.customer?.company?.legalName ||
          item.customer?.company?.name ||
          "-",
        customer: item.customer?.name || "-",
        vatCode: item.customer?.vatCode || "-",
        customerId: item.customer?.id,
        companyId: companyId,
        assignedUser: item.customer?.assignedUser?.name || "-",
        manager:
          item.manager?.name || item.companyUserEntity?.manager?.name || "-",
        year: item.finance?.year || item.year || "-",
        createdAt: item.finance?.createdAt || item.createdAt,
        updatedAt: item.finance?.updatedAt || item.updatedAt,
        isApproved: item.finance?.isApproved || false,
        status: item.finance?.isApproved ? "approved" : "pending",
        tone: item.finance?.isApproved ? "success" : "attention",
      })) || [];
  const isLoadingFilteredApprovals = approvalsCollection.length === 0 && hasMore;

  const rowUrl = (item: any): string | undefined => {
    const customerId = item.customerId || item.id;
    const itemCompanyId = item.companyId || companyId;
    if (!itemCompanyId || !customerId) return undefined;
    if (item.year && item.year !== "-" && item.financeId) {
      return `/app/${itemCompanyId}/customers/${customerId}/finance/${item.year}/${item.financeId}`;
    }
    return `/app/${itemCompanyId}/customers/${customerId}`;
  };

  // One way in per width: a phone card opens as a whole (rowUrl); the table keeps its cells as
  // buttons, which a keyboard and a screen reader can reach.
  const handleRowClick = (item: any) => {
    const url = rowUrl(item);
    if (url) navigate(url);
  };
  const onCellClick = isPhone ? undefined : handleRowClick;

  if (!hasLoadedOnce && loading && collection.length === 0 && companyId) {
    return <Loading />;
  }

  return (
    <div className="space-y-4">
      {loadError && (
        <div className="form-warnings">
          <Banner tone="critical">{t("not_fetch")}</Banner>
        </div>
      )}

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-2 items-end">
          <div className="md:col-span-2">
            <TextField
              label={t("search", { defaultValue: "Search" })}
              value={textSearch}
              onChange={setTextSearch}
              placeholder={t("pending_approvals_search_placeholder", {
                defaultValue: "Search by customer, company, or VAT code...",
              })}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setTextSearch("")}
              helpText={
                isSearchPending
                  ? t("unsigned_files_search_min_chars", {
                      defaultValue: "Type at least 2 characters to search",
                    })
                  : undefined
              }
            />
          </div>
          <div>
            <Select
              label={t("assigned_user", { defaultValue: "Assigned User" })}
              options={[
                {
                  label: t("all_assigned_users", { defaultValue: "All Users" }),
                  value: "",
                },
                ...assignedUserOptions.map((user) => ({
                  label: user.name,
                  value: user.id,
                })),
              ]}
              value={assignedUserFilter}
              onChange={setAssignedUserFilter}
            />
          </div>
          <div>
            <Select
              label={t("status", { defaultValue: "Status" })}
              options={[
                {
                  label: t("all_statuses", { defaultValue: "All Statuses" }),
                  value: "",
                },
                ...statusOptions.map((status) => ({
                  label: t(status, { defaultValue: status }),
                  value: status,
                })),
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </div>
        </div>
      </Card>

      {approvalsCollection.length > 0 || isLoadingFilteredApprovals ? (
        <Card>
          <div
            ref={scrollContainerRef}
            className={isPhone ? undefined : "max-h-[calc(100dvh-280px)] overflow-y-auto"}
          >
            <DataTable
              collection={approvalsCollection}
              rowUrl={isPhone ? rowUrl : undefined}
              columns={[
                { key: "#" },
                {
                  key: "company",
                  name: t("company"),
                  translationRequired: true,
                  onClick: onCellClick,
                  mobileView: "topLeft",
                },
                {
                  key: "customer",
                  name: t("customer"),
                  translationRequired: true,
                  onClick: onCellClick,
                  mobileView: "bottomLeft",
                },
                {
                  key: "vatCode",
                  name: t("vat_code"),
                  translationRequired: true,
                  onClick: onCellClick,
                },
                {
                  key: "assignedUser",
                  name: t("assigned_user"),
                  translationRequired: true,
                  onClick: onCellClick,
                },
                {
                  key: "manager",
                  name: t("manager"),
                  translationRequired: true,
                  onClick: onCellClick,
                },
                {
                  key: "year",
                  name: t("year"),
                  translationRequired: true,
                  onClick: onCellClick,
                },
                {
                  key: "status",
                  name: t("status") || "Status",
                  type: "badge",
                  translationRequired: true,
                  mobileView: "bottomRight",
                },
                {
                  key: "updatedAt",
                  name: t("assigned_date"),
                  type: "datetime",
                  onClick: onCellClick,
                  mobileView: "topRight",
                },
              ]}
            />
            <div ref={sentinelRef} className="h-4" aria-hidden />
            {(loadingMore ||
              isLoadingFilteredApprovals ||
              (loading && collection.length > 0)) && (
              <div className="flex justify-center py-4">
                <Spinner size="small" accessibilityLabel={t("loading")} />
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card>
          <Text as="p">
            {hasActiveFilters
              ? t("no_approvals_match_filters", {
                  defaultValue: "No approvals match your filters.",
                })
              : t("no_pending_approvals")}
          </Text>
        </Card>
      )}
    </div>
  );
};

export default PendingApprovalsContainer;
